import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import SocialLogin from '../../components/auth/SocialLogin';

import PersonalInfoForm from './components/PersonalInfoForm';
import WeddingDetailsForm from './components/WeddingDetailsForm';
import AccountSetupForm from './components/AccountSetupForm';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';

const SignUpPage = () => {
  const navigate = useNavigate();
  const { signUp, authError, loading } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [formData, setFormData] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSocialLogin, setShowSocialLogin] = useState(true);

  // Auto-save functionality
  useEffect(() => {
    const savedData = localStorage.getItem('userRegistrationData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(parsed.formData || {});
        setCurrentStep(parsed.currentStep || 1);
        setCompletedSteps(parsed.completedSteps || []);
      } catch (error) {
        console.error('Error loading saved data:', error);
      }
    }
  }, []);

  // Save data to localStorage whenever formData changes
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      localStorage.setItem('userRegistrationData', JSON.stringify({
        formData,
        currentStep,
        completedSteps,
        lastSaved: new Date().toISOString()
      }));
      setHasUnsavedChanges(true);
    }
  }, [formData, currentStep, completedSteps]);

  const handleFormUpdate = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);
  };

  const handleStepClick = (stepId) => {
    if (completedSteps.includes(stepId) || stepId <= currentStep) {
      setCurrentStep(stepId);
    }
  };

  const handleSave = () => {
    setIsLoading(true);
    setTimeout(() => {
      setHasUnsavedChanges(false);
      setIsLoading(false);
    }, 1000);
  };

  const handleNext = () => {
    if (currentStep < 3) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps(prev => [...prev, currentStep]);
      }
      setCurrentStep(currentStep + 1);
      setHasUnsavedChanges(false);
      // Hide social login once user starts the form process
      if (currentStep === 1) {
        setShowSocialLogin(false);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Show social login again if user goes back to step 1
      if (currentStep === 2) {
        setShowSocialLogin(true);
      }
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    
    try {
      const result = await signUp(formData.email, formData.password, {
        fullName: formData.fullName,
        role: 'user',
        weddingDate: formData.weddingDate,
        location: formData.location,
        culturalBackground: formData.culturalBackground,
        phone: formData.phone,
        preferences: formData.preferences,
        referralCode: formData.referralCode,
        referralSource: formData.referralSource
      });
      
      if (result?.success) {
        setCompletedSteps(prev => [...prev, 3]);
        localStorage.removeItem('userRegistrationData');
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate('/user-dashboard');
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            {/* Social Login for Step 1 */}
            {showSocialLogin && (
              <div className="mb-8">
                <div className="text-center mb-6">
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                    Quick Sign Up
                  </h3>
                  <p className="text-text-secondary font-caption">
                    Get started instantly with your social account
                  </p>
                </div>
                
                <SocialLogin 
                  orientation="vertical"
                  showDivider={true}
                  dividerText="Or create account with email"
                  className="mb-6"
                />
              </div>
            )}
            
            <PersonalInfoForm
              formData={formData}
              onUpdate={handleFormUpdate}
              onNext={handleNext}
            />
          </div>
        );
      case 2:
        return (
          <WeddingDetailsForm
            formData={formData}
            onUpdate={handleFormUpdate}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        );
      case 3:
        return (
          <AccountSetupForm
            formData={formData}
            onUpdate={handleFormUpdate}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSubmit={handleSubmit}
            authError={authError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-surface-50">
      <Header />
      
      {/* Progress Header */}
      <div className="bg-white border-b border-border shadow-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="hidden lg:block py-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading font-bold text-2xl text-text-primary">
                  Create Your Account
                </h2>
                <p className="text-text-secondary font-caption mt-1">
                  Join ShaadiNest to plan your perfect wedding
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-sm font-medium text-text-primary">
                    {completedSteps.length} of 3 completed
                  </div>
                  <div className="text-xs text-text-secondary font-caption">
                    {Math.round((completedSteps.length / 3) * 100)}% complete
                  </div>
                </div>
                <div className="w-32 h-2 bg-surface-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-500"
                    style={{ width: `${(completedSteps.length / 3) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              {[
                { id: 1, title: 'Personal Information', description: 'Basic details and contact info', icon: 'User' },
                { id: 2, title: 'Wedding Details', description: 'Date, location, and preferences', icon: 'Calendar' },
                { id: 3, title: 'Account Setup', description: 'Password and final setup', icon: 'Shield' }
              ].map((step, index) => {
                const isCompleted = completedSteps.includes(step.id);
                const isCurrent = step.id === currentStep;
                const isAccessible = isCompleted || isCurrent || step.id < currentStep;
                
                return (
                  <div key={step.id} className="flex items-center flex-1">
                    <button
                      onClick={() => handleStepClick(step.id)}
                      disabled={!isAccessible}
                      className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-smooth ${
                        isCompleted
                          ? 'bg-success border-success text-success-foreground'
                          : isCurrent
                          ? 'bg-primary border-primary text-primary-foreground'
                          : isAccessible
                          ? 'bg-white border-text-secondary text-text-secondary hover:border-primary hover:text-primary' :'bg-surface-100 border-border text-text-muted cursor-not-allowed'
                      } ${isAccessible ? 'hover:scale-105' : ''}`}
                    >
                      <Icon 
                        name={isCompleted ? 'CheckCircle' : step.icon} 
                        size={20} 
                        color="currentColor" 
                      />
                      
                      {hasUnsavedChanges && isCurrent && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-warning rounded-full border-2 border-white" />
                      )}
                    </button>

                    <div className="ml-4 flex-1">
                      <div className={`font-medium text-sm ${
                        isCurrent ? 'text-primary' : 
                        isCompleted ? 'text-success' : 'text-text-secondary'
                      }`}>
                        {step.title}
                      </div>
                      <div className="text-xs text-text-muted font-caption">
                        {step.description}
                      </div>
                    </div>

                    {index < 2 && (
                      <div className={`flex-1 h-0.5 mx-4 ${
                        isCompleted ? 'bg-success' : 'bg-border'
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:hidden py-4">
            <div className="flex items-center justify-between mb-4">
              <span className="font-heading font-semibold">
                Step {currentStep} of 3
              </span>
              <div className="text-sm text-text-secondary font-data">
                {Math.round((completedSteps.length / 3) * 100)}%
              </div>
            </div>

            <div className="w-full h-2 bg-surface-200 rounded-full overflow-hidden mb-4">
              <div 
                className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-500"
                style={{ width: `${(completedSteps.length / 3) * 100}%` }}
              />
            </div>
          </div>

          <div className="flex items-center justify-between py-4 border-t border-border-light">
            <div className="flex items-center space-x-4">
              {hasUnsavedChanges && (
                <div className="flex items-center space-x-2 text-warning">
                  <Icon name="AlertCircle" size={16} color="currentColor" />
                  <span className="text-sm font-caption">Unsaved changes</span>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="md"
                onClick={handleSave}
                disabled={isLoading || !hasUnsavedChanges}
                iconName="Save"
                iconSize={16}
              >
                Save Progress
              </Button>
              
              {currentStep > 1 && (
                <Button
                  variant="outline"
                  size="md"
                  onClick={handlePrevious}
                  disabled={isLoading}
                  iconName="ArrowLeft"
                  iconSize={16}
                >
                  Previous
                </Button>
              )}
              
              <Button
                variant="primary"
                size="md"
                onClick={currentStep === 3 ? handleSubmit : handleNext}
                disabled={isLoading}
                loading={isLoading}
                iconName="ArrowRight"
                iconPosition="right"
                iconSize={16}
              >
                {currentStep === 3 ? 'Create Account' : 'Next Step'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-soft border border-border overflow-hidden">
          <div className="p-6 sm:p-8 lg:p-12">
            {renderCurrentStep()}
          </div>
        </div>
      </main>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-strong max-w-md w-full p-8 text-center animate-slide-up">
            <div className="flex items-center justify-center w-16 h-16 bg-success-50 rounded-full mx-auto mb-6">
              <Icon name="CheckCircle" size={32} color="#228B22" />
            </div>
            
            <h2 className="font-heading font-bold text-2xl text-text-primary mb-4">
              Welcome to ShaadiNest!
            </h2>
            
            <p className="text-text-secondary font-caption mb-6">
              Your account has been created successfully. You can now start exploring 
              vendors and planning your perfect wedding celebration.
            </p>
            
            <div className="space-y-3">
              <Button
                variant="primary"
                size="lg"
                onClick={handleSuccessClose}
                iconName="ArrowRight"
                iconPosition="right"
                iconSize={18}
                fullWidth
              >
                Start Planning
              </Button>
              
              <Button
                variant="outline"
                size="md"
                onClick={() => navigate('/homepage')}
                iconName="Home"
                iconSize={16}
                fullWidth
              >
                Explore Homepage
              </Button>
            </div>
            
            <div className="mt-6 p-4 bg-surface-50 rounded-lg">
              <p className="text-sm text-text-secondary font-caption">
                <strong>What's next?</strong><br />
                • Complete your wedding details<br />
                • Browse and save your favorite vendors<br />
                • Use our planning tools to stay organized
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Help & Support */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-soft border border-border p-6">
          <div className="flex items-start space-x-4">
            <div className="flex items-center justify-center w-10 h-10 bg-secondary-50 rounded-lg flex-shrink-0">
              <Icon name="HelpCircle" size={20} color="#DAA520" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-text-primary mb-2">
                Need Help?
              </h3>
              <p className="text-text-secondary font-caption mb-4">
                Already have an account? <Link to="/sign-in-page" className="text-primary hover:underline">Sign in here</Link>
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="MessageCircle"
                  iconSize={16}
                >
                  Live Chat
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Mail"
                  iconSize={16}
                >
                  Email Support
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="text-center">
          <p className="text-sm text-text-muted font-caption">
            By creating an account, you're joining thousands of couples who have planned their dream South Asian weddings with ShaadiNest.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SignUpPage;